import {
  emptyAccountBalance,
  FlowTrackerSteps,
  INITIATE_TRANSFER_FAQ,
  serviceTermsUrl,
} from 'common/constants';
import { ATBeaconLoadingSpinner } from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFastLinkLogMutation } from 'src/api/atv/atvApi';
import Accordion from 'src/components/common/Accordion/Accordion';
import FastLink from 'src/components/common/Yodlee/FastLink';
import {
  addStepToHistory,
  setCurrentStep,
  setSelectedExternalAccount,
  setTransferFlow,
} from 'src/features/transfer/transferSlice';
import { setProviderAccountID } from 'src/features/transfer/yodleeConfigSlice';
import { EventType, TransferFlow } from 'src/utils/constants';
import { dispatchPageLoad } from 'utils/analyticsUtil';
import { TransferFlowStep } from '..';
import { useYodleeInit } from '../../hooks/useYodleeInit';
import { IExternalAccountState } from '../../transferSlice.types';
import '../chooseAccountType/ChooseAccountType.scss';
import './InitiateTransfer.scss';

interface InitiateTransferProps {
  setIsStepValid: (isEnabled: boolean) => void;
}

const InitiateTransfer: React.FC<InitiateTransferProps> = (props) => {
  const { setIsStepValid } = props;
  const { isFastLinkInitialized, yodleeData, YodleeUrl, configName, obAppName } =
    useYodleeInit();
  const dispatch = useDispatch();
  const [fastLinkLog] = useFastLinkLogMutation();

  const handleNoFastlinkResults = (_hasNoResults: boolean) => {
    // Placeholder for future search results handling
  };

  const goToManualEntry = (event: any) => {
    event.preventDefault();
    const noExternalAccountSelected: IExternalAccountState = {
      selectedAccountType: 'noAccountType',
      selectedId: -1,
      noAccounts: true,
      institutionName: '',
      accountNumber: '',
      balance: emptyAccountBalance,
      missingFields: [],
      holderFullNames: [],
    };
    dispatch(addStepToHistory(TransferFlowStep.INITIATE_TRANSFER));
    dispatch(setSelectedExternalAccount(noExternalAccountSelected));
    dispatch(setTransferFlow(TransferFlow.Manual));
    dispatch(setCurrentStep(TransferFlowStep.MANUAL_TRANSFER));
  };

  const memoizedFastLink = useMemo(() => {
    if (!isFastLinkInitialized || !yodleeData) {
      return null;
    }

    return (
      <FastLink
        fastLinkURL={yodleeData?.fastLinkUrl ?? ''}
        accessToken={yodleeData?.token ?? ''}
        YodleeUrl={YodleeUrl}
        configName={configName}
        obAppName={obAppName}
        onSuccess={(data) => {
          fastLinkLog({
            message: JSON.stringify(data),
            eventType: EventType.SUCCESS,
            id: yodleeData.id,
          });
          dispatch(setProviderAccountID(data.providerAccountId));
          dispatch(addStepToHistory(TransferFlowStep.INITIATE_TRANSFER));
          dispatch(setCurrentStep(TransferFlowStep.SELECT_EXTERNAL_ACCOUNT));
          setIsStepValid(false);
        }}
        onError={(error) => {
          fastLinkLog({
            message: JSON.stringify(error),
            eventType: EventType.ERROR,
            id: yodleeData.id,
          });
        }}
        onClose={(data) => {
          fastLinkLog({
            message: JSON.stringify(data),
            eventType: EventType.CLOSE,
            id: yodleeData.id,
          });
        }}
        onEvent={(data) => {
          const { EVENT_NAME, SEARCH_RESULTS } = data.props || {};
          switch (EVENT_NAME || data.eventName) {
            case 'FASTLINK_SELECT-SITE_CLICK_SEARCH-BAR':
              handleNoFastlinkResults(SEARCH_RESULTS === 'NONE');
              break;
            case 'FASTLINK_COMMON_BUTTON_BACK':
              handleNoFastlinkResults(false);
              break;
            case 'FASTLINK_OB-CONSENT_VIEW_PAGE-LOAD':
              dispatchPageLoad('ExternalAccount|Institution');
              break;
            default:
              break;
          }
        }}
      />
    );
  }, [
    isFastLinkInitialized,
    yodleeData,
    YodleeUrl,
    configName,
    obAppName,
    fastLinkLog,
    dispatch,
    setIsStepValid,
  ]);

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid">
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            <div className="b-typography-sans-serif--headline--50 b-font-weight-bold">
              Search for your current financial firm
            </div>
            <div className="no-search-results">
              Can&apos;t find your institution? &nbsp;
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                onClick={goToManualEntry}
              >
                Enter your details manually
              </a>
            </div>
            {memoizedFastLink || (
              <div className="fast-link-loading-space">
                <ATBeaconLoadingSpinner scale={'jumbo'} />
              </div>
            )}
            <div className="legal-disclosure b-typography-sans-serif--headline--50 b-font-weight-light">
              The asset transfer verification service, powered by Yodlee, grants access to
              and shares data of financial accounts you hold outside of T. Rowe Price with
              T. Rowe Price and is governed by the{' '}
              <a href={serviceTermsUrl} target="_blank" rel="noopener noreferrer">
                Service Terms
              </a>
              .
            </div>
          </div>
        </div>
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={0} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={INITIATE_TRANSFER_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default InitiateTransfer;
